import type {ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {buildProgressiveDiffEntries} from '../../utils/build-progressive-diff-entries'
import type {StateCreator} from 'zustand'
import type {DiffEntriesSlice} from './diff-entries'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'
import {makeProgressiveDiffLoader} from '../../utils/progressive-diffs-loader'

export interface ProgressiveDiffsSlice {
  // Hashmap by pathDigest
  entriesMap: Record<string, ProgressiveDiffEntry>
  // Ordered list of pathDigests to maintain iteration order
  entriesOrder: string[]

  selectedPathDigest: string | undefined

  // Getter methods to expose external state when called (no Zustand subscription)
  getSelectedPathDigest: () => string | undefined

  getProgressiveDiffEntryByPathDigest: (pathDigest?: string) => ProgressiveDiffEntry | undefined

  // Public actions, intended for use outside the store.
  progressiveDiffsActions: {
    startQueue: (options?: {autoLoadAllDiffs?: boolean}) => void
    loadPriority: (pathDigests: string[]) => void
    loadMore: (pathDigestOrEntry: string | ProgressiveDiffEntry, options?: {preferAnySoloEntry?: boolean}) => void
    updateSelectedPathDigest: (pathDigest: string | undefined) => void
    /** @deprecated Use updateSelectedPathDigest + loadMore instead */
    updateSelectedPathDigestAndFetch: (pathDigest: string | undefined) => void
  }
}

export type ProgressiveDiffSliceOpts = {
  initialLoadedPathDigests: Set<string>
  selectedPathDigest: string | undefined
} & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>

export const createProgressiveDiffsSlice =
  ({
    initialDiffSummaries,
    initialLoadedPathDigests,
    selectedPathDigest: initialSelectedPathDigest,
  }: ProgressiveDiffSliceOpts): StateCreator<
    ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice,
    [['zustand/devtools', never]],
    [],
    ProgressiveDiffsSlice
  > =>
  (set, get) => {
    const progressiveDiffLoader = makeProgressiveDiffLoader({get, initialLoadedPathDigests})

    // Build initial entries
    const {entriesMap: initialEntriesMap, entriesOrder: initialEntriesOrder} = buildProgressiveDiffEntries({
      diffSummaries: initialDiffSummaries,
      loadedPathDigests: initialLoadedPathDigests,
      selectedPathDigest: initialSelectedPathDigest,
    })

    return {
      entriesMap: initialEntriesMap,
      entriesOrder: initialEntriesOrder,
      selectedPathDigest: initialSelectedPathDigest,

      getSelectedPathDigest: () => get().selectedPathDigest,
      getProgressiveDiffEntryByPathDigest: (pathDigest?: string) =>
        pathDigest ? get().entriesMap[pathDigest] : undefined,
      progressiveDiffsActions: {
        startQueue: progressiveDiffLoader.startQueue,
        loadPriority: progressiveDiffLoader.loadPriority,
        loadMore: progressiveDiffLoader.loadMore,
        updateSelectedPathDigest: (pathDigest: string | undefined) => {
          set({selectedPathDigest: pathDigest}, undefined, 'updateSelectedPathDigest')
        },
        /** @deprecated Use updateSelectedPathDigest + loadMore instead */
        updateSelectedPathDigestAndFetch: (pathDigest: string | undefined) => {
          set({selectedPathDigest: pathDigest}, undefined, 'updateSelectedPathDigest')
          if (pathDigest) {
            progressiveDiffLoader.loadMore(pathDigest)
          }
        },
      },
    }
  }
